'use client';

import { useEffect, useRef } from 'react';
import { FallingNotesRenderer, BambooNote } from '@/components/Bamboo/FallingNotesRenderer';
import { ManagerEntity } from '@/shared/globalFunctions';
import displayModeStore from '@/stores/displayModeStore';
import { notesStatusEvents } from '@/sharedTypes/coreTypes';

/**
 * Extract all notes from the timemap into a flat BambooNote array.
 * Defined outside the hook to avoid stale closures — always reads fresh from ManagerEntity.
 */
function extractNotes(): BambooNote[] {
  const core = ManagerEntity?.core;
  if (!core || !core.timemap) return [];

  const notes: BambooNote[] = [];
  for (const measure of core.timemap) {
    if (!measure.notes || !Array.isArray(measure.notes)) continue;
    for (const note of measure.notes) {
      if (!note.midi) continue;

      const startTime = note.noteAbsoluteTime;
      const endTime = note.midi.timeNoteOff
        ? (note.midi.timeNoteOff - note.midi.time + startTime)
        : startTime + Math.max(note.midi.duration || 0, 100);

      notes.push({
        noteId: note.noteId,
        midi: note.midi.pitch,
        startTime,
        endTime,
        hand: note.hand ?? 0,
        measureIndex: note.measureIndex,
      });
    }
  }
  return notes;
}

/**
 * Hook that manages the FallingNotesRenderer lifecycle and syncs it
 * with the Player/Runner state.
 *
 * IMPORTANT: This hook reads `displayModeStore.showBamboo` during render.
 * The parent component (BambooCanvas) must be wrapped with MobX `observer`
 * so that changes to showBamboo trigger a re-render, which then triggers
 * this useEffect AFTER React commits the DOM (containerRef is valid).
 */
export function useBamboo(containerRef: React.RefObject<HTMLDivElement | null>) {
  const rendererRef = useRef<FallingNotesRenderer | null>(null);
  const rafRef = useRef<number | null>(null);
  const isActiveRef = useRef(false);

  // Read showBamboo and displayMode during render — MobX observer on the parent
  // component will track these and re-render when they change.
  // showBamboo is true for both 'bamboo' and 'split' modes.
  const showBamboo = displayModeStore.showBamboo;
  const displayMode = displayModeStore.displayMode;

  // Initialize / destroy renderer when display mode changes.
  // useEffect runs AFTER React commits the DOM, so containerRef.current is valid.
  useEffect(() => {
    const container = containerRef.current;

    if (showBamboo && container && !rendererRef.current) {
      console.log('[Bamboo] Creating renderer...');
      const renderer = new FallingNotesRenderer();
      rendererRef.current = renderer;
      isActiveRef.current = true;

      const startPositionSync = () => {
        const sync = () => {
          if (!isActiveRef.current || !rendererRef.current) return;

          const player = ManagerEntity?.player;
          const runner = player?.runner;
          if (runner) {
            // Pass playback state so renderer can extrapolate smoothly
            const isPlaying = !!player.isPlaying;
            const speed = player.timer?.speed ?? 5;
            rendererRef.current.setPlaybackState(isPlaying, speed);

            const timemap = runner._timemap;
            const measure = runner.measure;
            if (timemap && measure && timemap[measure.index] !== undefined) {
              const absoluteTime = timemap[measure.index].start + measure.offset;
              rendererRef.current.setPosition(absoluteTime);
            }
          }

          rafRef.current = requestAnimationFrame(sync);
        };
        rafRef.current = requestAnimationFrame(sync);
      };

      renderer.init(container).then(() => {
        console.log('[Bamboo] Renderer initialized, loading notes...');
        const notes = extractNotes();
        console.log('[Bamboo] Extracted notes:', notes.length);
        if (notes.length > 0) {
          renderer.loadNotes(notes);
        }
        startPositionSync();
      }).catch((err) => {
        console.error('[Bamboo] Renderer init FAILED:', err);
      });
    }

    return () => {
      if (rendererRef.current) {
        console.log('[Bamboo] Destroying renderer...');
        isActiveRef.current = false;
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        rendererRef.current.destroy();
        rendererRef.current = null;
      }
    };
  }, [showBamboo, containerRef]);

  // Resize renderer when switching between bamboo ↔ split.
  // The container CSS changes (top: 0 vs top: 33%) but no window resize fires.
  useEffect(() => {
    if (!rendererRef.current || !showBamboo) return;
    // Small delay to let CSS update first
    const raf = requestAnimationFrame(() => {
      rendererRef.current?.resize();
    });
    return () => cancelAnimationFrame(raf);
  }, [displayMode, showBamboo]);

  // Update sheet music visible viewport when display mode changes.
  // In split mode, bamboo covers the bottom portion, so sheet auto-scroll
  // should center within the visible top area only.
  // Also add bottom padding so the last system can scroll into the visible area.
  useEffect(() => {
    const renderer = ManagerEntity?.renderer;
    if (!renderer || typeof renderer.setVisibleViewportBottom !== 'function') return;

    const playerSheet = document.getElementById('player-sheet');

    if (displayMode === 'split') {
      // Bamboo starts at 50% of window height in split mode
      const visibleHeight = window.innerHeight * 0.5;
      renderer.setVisibleViewportBottom(visibleHeight);

      // Add bottom padding so the last system can be scrolled up
      // above the bamboo overlay into the visible area
      if (playerSheet) {
        playerSheet.style.paddingBottom = `${visibleHeight}px`;
      }
    } else {
      // Full viewport available (sheet-only or bamboo-only where sheet is hidden)
      renderer.setVisibleViewportBottom(null);
      if (playerSheet) {
        playerSheet.style.paddingBottom = '';
      }
    }

    return () => {
      // Clean up padding when unmounting
      if (playerSheet) {
        playerSheet.style.paddingBottom = '';
      }
    };
  }, [displayMode]);

  // Listen for sample-loaded to reload notes
  useEffect(() => {
    const handleSampleLoaded = () => {
      if (!rendererRef.current || !isActiveRef.current) return;
      const notes = extractNotes();
      rendererRef.current.loadNotes(notes);
      rendererRef.current.reset();
    };

    window.addEventListener(notesStatusEvents.SAMPLE_LOADED, handleSampleLoaded);
    return () => window.removeEventListener(notesStatusEvents.SAMPLE_LOADED, handleSampleLoaded);
  }, []);

  // Listen for note success/fail events
  useEffect(() => {
    const handleSuccess = (e: Event) => {
      if (!isActiveRef.current || !rendererRef.current) return;
      const detail = (e as CustomEvent)?.detail;
      if (detail?.pitch != null) {
        // Per-note success: mark individual note and flash green
        rendererRef.current.onNoteSuccessByPitch(detail.pitch);
        rendererRef.current.spawnHitEffect(detail.pitch, 0x33aa55);
      } else if (detail?.measureIndex !== undefined) {
        rendererRef.current.onMeasureNotesSuccess(detail.measureIndex);
      }
    };

    const handleFail = (e: Event) => {
      if (!isActiveRef.current || !rendererRef.current) return;
      const detail = (e as CustomEvent)?.detail;
      if (detail?.pitch != null) {
        // Per-note fail: mark note and flash red on the pressed key
        rendererRef.current.onNoteFailByPitch(detail.pitch);
        rendererRef.current.spawnHitEffect(detail.pitch, 0xdd3333);
      } else if (detail?.measureIndex !== undefined) {
        rendererRef.current.onMeasureNotesFail(detail.measureIndex);
      }
    };

    window.addEventListener(notesStatusEvents.NOTE_SUCCESS, handleSuccess);
    window.addEventListener(notesStatusEvents.NOTE_FAILED, handleFail);

    return () => {
      window.removeEventListener(notesStatusEvents.NOTE_SUCCESS, handleSuccess);
      window.removeEventListener(notesStatusEvents.NOTE_FAILED, handleFail);
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      rendererRef.current?.resize();

      // Update visible viewport bottom and padding on resize when in split mode
      if (displayModeStore.isSplit) {
        const visibleHeight = window.innerHeight * 0.5;
        const renderer = ManagerEntity?.renderer;
        if (renderer && typeof renderer.setVisibleViewportBottom === 'function') {
          renderer.setVisibleViewportBottom(visibleHeight);
        }
        const playerSheet = document.getElementById('player-sheet');
        if (playerSheet) {
          playerSheet.style.paddingBottom = `${visibleHeight}px`;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Highlight piano keys when the user presses MIDI keys.
  // Core's Player.noteOn/noteOff dispatch 'midiKeyDown'/'midiKeyUp' events.
  useEffect(() => {
    const handleKeyDown = (e: Event) => {
      if (!isActiveRef.current || !rendererRef.current) return;
      const midi = (e as CustomEvent)?.detail?.midi;
      if (midi != null) rendererRef.current.pressKey(midi);
    };

    const handleKeyUp = (e: Event) => {
      if (!isActiveRef.current || !rendererRef.current) return;
      const midi = (e as CustomEvent)?.detail?.midi;
      if (midi != null) rendererRef.current.releaseKey(midi);
    };

    window.addEventListener('midiKeyDown', handleKeyDown);
    window.addEventListener('midiKeyUp', handleKeyUp);

    return () => {
      window.removeEventListener('midiKeyDown', handleKeyDown);
      window.removeEventListener('midiKeyUp', handleKeyUp);
    };
  }, []);

  // Handle playback stop/rewind
  useEffect(() => {
    const handleRewind = () => {
      if (isActiveRef.current) {
        rendererRef.current?.reset();
      }
    };

    window.addEventListener('playbackRewind', handleRewind);
    window.addEventListener('playbackStopped', handleRewind);

    return () => {
      window.removeEventListener('playbackRewind', handleRewind);
      window.removeEventListener('playbackStopped', handleRewind);
    };
  }, []);

  return rendererRef;
}
